import React from "react";

const CHUNK_RELOAD_KEY = "sysigreja_chunk_reload";
const CHUNK_RELOAD_JANELA_MS = 30_000;

type LazyModule = Record<string, unknown> & { default?: React.ComponentType<unknown> };

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : String(error ?? "");

  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Unable to preload CSS/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

/** Reload one-shot após falha de chunk (aba com bundle antigo pós-deploy). */
export function recoverFromChunkLoadError(meta?: {
  label?: string;
  route?: string;
  origem?: string;
}): boolean {
  try {
    // Trava por janela de tempo: um reload por falha. Se o chunk ainda falhar logo
    // apos o reload, para (evita loop). Um deploy novo mais tarde na mesma aba
    // volta a poder recarregar (a trava antiga "1" ficava presa a sessao toda).
    const ultimo = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    if (Number.isFinite(ultimo) && Date.now() - ultimo < CHUNK_RELOAD_JANELA_MS) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    /* sessionStorage indisponível — ainda tenta reload */
  }

  void import("@/lib/logs")
    .then(({ postLog }) =>
      postLog({
        Tipo: "Frontend_ChunkLoad_Recuperacao",
        Objeto: {
          acao: "reload_one_shot",
          label: meta?.label,
          route: meta?.route ?? (typeof window !== "undefined" ? window.location.pathname : undefined),
          origem: meta?.origem ?? "lazy_with_retry",
        },
      }),
    )
    .catch(() => undefined);

  window.location.reload();
  return true;
}

export function clearChunkReloadGuard(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    /* ignore */
  }
}

function isReactComponentType(value: unknown): value is React.ComponentType<unknown> {
  if (typeof value === "function") return true;
  if (typeof value === "object" && value !== null) {
    return typeof (value as { $$typeof?: unknown }).$$typeof === "symbol";
  }
  return false;
}

async function loadWithRetry(
  loader: () => Promise<LazyModule>,
  retries = 2,
): Promise<LazyModule> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await loader();
    } catch (error) {
      lastError = error;
      if (!isChunkLoadError(error) || attempt === retries) {
        throw error;
      }
      await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
    }
  }
  throw lastError;
}

/**
 * React.lazy com retry e reload one-shot em falha de chunk (pós-deploy Vite).
 */
export function lazyWithRetry(
  loader: () => Promise<LazyModule>,
  label: string,
  fallbackNamedExport?: string,
): React.LazyExoticComponent<React.ComponentType<unknown>> {
  return React.lazy(async () => {
    try {
      const module = await loadWithRetry(loader);
      const Component =
        module.default ??
        (fallbackNamedExport ? module[fallbackNamedExport] : undefined);

      if (!isReactComponentType(Component)) {
        const exportKeys = Object.keys(module)
          .filter((key) => key !== "__esModule")
          .join(", ");
        throw new Error(
          `${label}: export default inválido ou ausente${exportKeys ? ` (exports: ${exportKeys})` : ""}`,
        );
      }

      clearChunkReloadGuard();
      return { default: Component };
    } catch (error) {
      if (isChunkLoadError(error)) {
        recoverFromChunkLoadError({ label, origem: "lazyWithRetry" });
      }
      throw error;
    }
  });
}

/** Vite dispara `vite:preloadError` quando um chunk/CSS pre-carregado da 404 (build antiga). */
export function instalarHandlerPreloadError(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("vite:preloadError", (event) => {
    if (recoverFromChunkLoadError({ origem: "vite:preloadError" })) {
      event.preventDefault();
    }
  });
}
